/**
 * The form model's field errors (TMM-B5, FRM-004/005, TMMRSL-0071). A failed submit - the client's
 * check or the server's field map - shows each failing field's message next to it and moves focus to
 * the first one in `order` (the visual order) through `useFocusOnError`. A field's error clears as soon
 * as its value changes (`clear`); nothing shows before the first submit. Save is never disabled for
 * invalid input: validity lives in the submit handler, which calls `fail` and returns without a request.
 */
import { useCallback, useState, type RefObject } from 'react';
import { useFocusOnError } from './useFocusOnError';

export interface FieldErrors<F extends string> {
  /** The message per failing field. */
  errors: Partial<Record<F, string>>;
  /** Show `next` (replacing the current errors) and focus the first of them. */
  fail: (next: Partial<Record<F, string>>) => void;
  /**
   * Show the fields the server named (`mapFieldErrors`), each with its own message from `messages`,
   * and focus the first. Returns whether any field was marked.
   */
  failMarked: (marked: Partial<Record<F, true>>, messages: Record<F, string>) => boolean;
  /** Show one field's error without moving focus (a blur check on an edited field). */
  show: (field: F, message: string) => void;
  /** The field's value changed: its error no longer applies. */
  clear: (field: F) => void;
  /** Drop every error (a new submit, or a dialog reopening). */
  reset: () => void;
}

export function useFieldErrors<F extends string>(
  order: readonly F[],
  refs: Partial<Record<F, RefObject<HTMLElement | null>>>,
  submitting: boolean,
): FieldErrors<F> {
  const [errors, setErrors] = useState<Partial<Record<F, string>>>({});
  const [focusField, setFocusField] = useState<F | null>(null);
  useFocusOnError(submitting, focusField, refs, () => setFocusField(null));

  const fail = useCallback(
    (next: Partial<Record<F, string>>) => {
      setErrors(next);
      setFocusField(order.find((field) => next[field] !== undefined) ?? null);
    },
    [order],
  );
  const failMarked = useCallback(
    (marked: Partial<Record<F, true>>, messages: Record<F, string>) => {
      const next: Partial<Record<F, string>> = {};
      for (const field of order) {
        if (marked[field] === true) next[field] = messages[field];
      }
      fail(next);
      return Object.keys(next).length > 0;
    },
    [order, fail],
  );
  const show = useCallback((field: F, message: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  }, []);
  const clear = useCallback((field: F) => {
    setErrors((prev) => {
      if (prev[field] === undefined) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);
  const reset = useCallback(() => {
    setErrors({});
    setFocusField(null);
  }, []);

  return { errors, fail, failMarked, show, clear, reset };
}
